import React, { useState, useEffect } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import hfaBackground from "../images/hfa_background.png";
import hfaLogo from "../images/hfa_logo.png";
import { ReactComponent as HammerStrengthLogo } from "../images/hammer_strength_logo.svg";
import { ReactComponent as GrayDivider } from "../images/gray_divider.svg";
import { ReactComponent as LifeFitnessLogo } from "../images/life_fitness_logo.svg";
import { ReactComponent as FacilityConnect } from "../images/facility_connect.svg";
import hfaGraphLines from "../images/hfa_graph_lines.svg";
import hfaGraphWave1 from "../images/hfa_graph_wave1.svg";
import hfaGraphWave2 from "../images/hfa_graph_wave2.svg";
import hfaDonutOuter from "../images/hfa_donut_outer.svg";
import hfaDonutTrack from "../images/hfa_donut_track.svg";
import hfaDonutInner from "../images/hfa_donut_inner.svg";
import hfaBarLines from "../images/hfa_bar_lines.svg";

function formatTime(date) {
  const day = date.getDate();
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${day} ${month} ${year} | ${hours}:${minutes} ${ampm}`;
}

function MetricCard({ label, value, className = "" }) {
  return (
    <div
      className={`backdrop-blur-[6px] border border-[#b8b8b8] flex flex-col gap-[18px] px-[42px] py-[30px] rounded-[12px] ${className}`}
    >
      <p className="font-['Montserrat'] font-semibold text-[#8b8b8b] text-[20px] tracking-[0.4px] uppercase">
        {label}
      </p>
      <p className="font-['Montserrat'] font-bold text-[84px] text-black tracking-[1.68px] leading-none">
        {value}
      </p>
    </div>
  );
}

export default function HFAPage() {
  const [now, setNow] = useState(new Date());
  const [athenaStats, setAthenaStats] = useState(null);
  const [cardioStats, setCardioStats] = useState(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "athena_stats", "latest"),
      (snap) => {
        if (snap.exists()) {
          setAthenaStats(snap.data());
        }
      }
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "cardio_stats", "latest"),
      (snap) => {
        if (snap.exists()) {
          setCardioStats(snap.data());
        }
      }
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    function updateScale() {
      const sx = window.innerWidth / 1920;
      const sy = window.innerHeight / 1080;
      setScale(Math.max(sx, sy));
    }
    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, []);

  return (
    <div
      className="w-screen h-screen overflow-hidden"
      style={{
        background:
          "linear-gradient(to left, rgb(250, 250, 250) 0%, rgb(241, 241, 241) 100%)",
      }}
    >
    <div
      className="relative font-['Montserrat']"
      style={{
        width: 1920,
        height: 1080,
        transform: `scale(${scale})`,
        transformOrigin: "top left",
      }}
    >
      {/* Background image - right side, faded */}
      <div className="absolute inset-0">
        <div
          className="absolute right-0 top-0 w-[52%] h-full"
          style={{
            maskImage:
              "linear-gradient(to right, transparent 0%, black 40%)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0%, black 40%)",
          }}
        >
          <img
            src={hfaBackground}
            alt=""
            className="w-full h-full object-cover opacity-40"
          />
        </div>
      </div>

      {/* Decorative background graphs */}
      <div className="absolute inset-0 mix-blend-luminosity pointer-events-none">
        {/* Line graph - bottom right */}
        <div className="absolute opacity-30" style={{ right: -213, top: 450, width: 671, height: 182 }}>
          <div className="relative w-full h-full">
            <img src={hfaGraphLines} alt="" className="absolute" style={{ left: '5%', top: '2%', width: '90%', height: '94%' }} />
            <img src={hfaGraphWave1} alt="" className="absolute" style={{ left: '5%', top: '5.5%', width: '90%', height: '91%' }} />
            <img src={hfaGraphWave2} alt="" className="absolute" style={{ left: '5%', top: '14%', width: '90%', height: '78%' }} />
          </div>
          <div className="absolute left-0 top-0 w-full h-full" style={{ backgroundImage: 'linear-gradient(90deg, rgb(244,244,244) 0%, rgba(244,244,244,0) 22%)' }} />
        </div>

        {/* Donut chart - middle */}
        <div className="absolute opacity-30" style={{ left: 1314, top: 284, width: 295, height: 204 }}>
          <div className="absolute flex items-center justify-center" style={{ left: 0, top: 0, width: 295, height: 204 }}>
            <div style={{ width: 150, height: 150, transform: 'rotate(-150.74deg)' }}>
              <img src={hfaDonutOuter} alt="" className="absolute inset-0 w-full h-full" />
              <img src={hfaDonutTrack} alt="" className="absolute inset-0 w-full h-full" />
              <img src={hfaDonutInner} alt="" className="absolute inset-0 w-full h-full" style={{ right: '8%', width: '92%' }} />
            </div>
          </div>
          <p className="absolute font-['Nunito_Sans'] font-bold text-[24px] text-black text-center leading-[32px]" style={{ top: '40%', left: '50%', transform: 'translate(-50%, -50%)' }}>
            80%
          </p>
          <p className="absolute font-['Nunito_Sans'] font-bold text-[12px] text-[#939598] text-center" style={{ top: '55%', left: '50%', transform: 'translate(-50%, -50%)' }}>
            Online
          </p>
          <div className="absolute" style={{ left: 0, top: 6, width: 295, height: 192, backgroundImage: 'linear-gradient(187deg, rgb(248,248,248) 6%, rgba(248,248,248,0) 93%)' }} />
        </div>

        {/* Bar chart - top right */}
        <div className="absolute opacity-30" style={{ left: 1641, top: 217, width: 310, height: 180 }}>
          <img src={hfaBarLines} alt="" className="absolute" style={{ left: 44, top: 4, width: 266, height: 171 }} />
          {/* Bars */}
          <div className="absolute rounded-t-[2px]" style={{ left: 75, top: 16, width: 15, height: 158, backgroundColor: '#f06b7c' }} />
          <div className="absolute rounded-t-[2px]" style={{ left: 142, top: 98, width: 15, height: 76, backgroundColor: '#6857bc' }} />
          <div className="absolute rounded-t-[2px]" style={{ left: 206, top: 36, width: 15, height: 138, backgroundColor: '#f28c38' }} />
          {/* Y Axis labels */}
          <div className="absolute font-['Nunito_Sans'] text-[10px] text-[#4d76bb]" style={{ left: 10, top: 0 }}>230K</div>
          <div className="absolute font-['Nunito_Sans'] text-[10px] text-[#4d76bb]" style={{ left: 0, top: 41 }}>172.5k</div>
          <div className="absolute font-['Nunito_Sans'] text-[10px] text-[#4d76bb]" style={{ left: 9, top: 82 }}>115k</div>
          <div className="absolute font-['Nunito_Sans'] text-[10px] text-[#4d76bb]" style={{ left: 6, top: 123 }}>57.5k</div>
          <div className="absolute font-['Nunito_Sans'] text-[10px] text-[#4d76bb]" style={{ left: 10, top: 165 }}>0</div>
        </div>
      </div>

      {/* Pagination dots */}
      <div className="absolute top-[20px] left-1/2 -translate-x-1/2 flex gap-[8px] items-center z-10">
        <div className="w-[6px] h-[6px] rounded-full bg-[#eb4a62]" />
        <div className="w-[6px] h-[6px] rounded-full bg-[#6d6d6d]" />
      </div>

      {/* Top-left: Logos */}
      <div className="absolute top-[83px] left-[71px] flex items-center gap-0 h-[30px]">
        <LifeFitnessLogo className="h-[21px] w-auto" />
        <GrayDivider className="h-[30px] mx-[10px]" />
        <HammerStrengthLogo className="h-[30px] w-auto" />
      </div>

      {/* Facility Connect */}
      <div className="absolute top-[140px] left-[71px]">
        <FacilityConnect className="h-[27px] w-auto" />
      </div>

      {/* Top-right: Timestamp */}
      <p className="absolute top-[85px] right-[68px] font-['Montserrat'] font-semibold text-[#8b8b8b] text-[18px] tracking-[1.8px] uppercase text-right">
        HFA Tradeshow | {formatTime(now)}
      </p>

      {/* Main Row: Marketing text + Total Workouts */}
      <div className="absolute top-[216px] left-[70px] flex gap-[50px] items-start z-10">
        <div className="w-[656px]">
          <p className="font-['Montserrat'] font-normal text-[32px] text-black leading-[48px]">
            Facility Connect is a free software solution that makes it easy to
            manage your Life Fitness equipment and make smart data driven
            decisions.
          </p>
        </div>
        <div className="backdrop-blur-[6px] border border-[#b8b8b8] flex flex-col gap-[18px] px-[42px] py-[30px] rounded-[12px] w-[502px]">
          <p className="font-['Montserrat'] font-semibold text-[#8b8b8b] text-[20px] tracking-[0.4px] uppercase">
            Total Tradeshow Workouts
          </p>
          <p className="font-['Montserrat'] font-bold text-[#a6192e] text-[132px] tracking-[2.64px] leading-none">
            {athenaStats?.totalWorkouts != null || cardioStats?.totalWorkouts != null
              ? ((athenaStats?.totalWorkouts ?? 0) + (cardioStats?.totalWorkouts ?? 0)).toLocaleString()
              : "—"}
          </p>
        </div>
      </div>

      {/* Secondary Data */}
      <div className="absolute top-[529px] left-[70px] flex flex-col gap-[50px] w-[1208px] z-10">
        {/* 1st Row */}
        <div className="flex gap-[60px] items-stretch">
          <MetricCard label="Total Minutes" value={cardioStats?.totalWorkoutDurationMinutes != null ? Math.round(cardioStats.totalWorkoutDurationMinutes).toLocaleString() : "—"} className="w-[332px]" />
          <MetricCard label="Miles Moved" value={cardioStats?.totalDistanceMiles != null ? cardioStats.totalDistanceMiles.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : "—"} className="w-[332px]" />
          <MetricCard label="Strength Reps" value={athenaStats?.totalReps?.toLocaleString() ?? "—"} className="w-[424px]" />
        </div>
        {/* 2nd Row */}
        <div className="flex gap-[60px] items-stretch">
          <MetricCard label="Calories Burned" value={cardioStats?.totalCalories != null ? Math.round(cardioStats.totalCalories).toLocaleString() : "—"} className="w-[576px]" />
          <MetricCard label="Data Points Collected" value={cardioStats?.totalWorkouts != null ? (cardioStats.totalWorkouts * 1300).toLocaleString() : "—"} className="w-[576px]" />
        </div>
      </div>

      {/* HFA Logo - bottom right, behind data */}
      <div className="absolute bottom-[111px] right-[68px] w-[366px] h-[336px] z-0">
        <img
          src={hfaLogo}
          alt="HFA Show"
          className="w-full h-full object-contain"
        />
      </div>
    </div>
    </div>
  );
}
